import renderInputField from 'components/FormControls/textInput'
import { Typography } from 'gu-ui-kit'
import PropTypes from 'prop-types'
import React, { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Field, reduxForm } from 'redux-form'
import shouldAsyncValidate from 'utils/shouldAsyncValidate'
import * as Yup from 'yup'
import { useAccountCreateFormSubmit } from 'modules/Forms/Account/Create/useAccountCreateFormSubmit'
import tinValidator from 'utils/validators/tinValidator'
import renderAmountField from 'components/FormControls/amountField'
import i18n from 'i18next'
import FormFrame from 'modules/Forms/Account/Create/subcomponents/FormFrame'
import { onlyNumbers } from 'utils/normalize/onlyNumbers'
import clsx from 'clsx'
import formStyles from 'modules/Forms/common-form.module.scss'

export const AvitoSchema = () =>
  Yup.object().shape({
    network: Yup.string().required('common::validation_messages.required'),
    avito_user_id: Yup.number().required('common::validation_messages.required'),
    tax_number: Yup.string()
      .test(
        'tinValidator',
        'services::forms.shared_fields.advertiser.fields.advertiser_info.tax_number.validation_messages.incorrect_tin_number',
        tinValidator,
      )
      .required('common::validation_messages.required'),
    budget: Yup.string()
      .test(
        'minAmount',
        i18n.t(
          'services::forms.register_account.avito.fields.budget.validation_messages.incorrect_amount_min',
          {
            value: '0.00',
            currency: 'RUB',
          },
        ),
        function test(value) {
          const { path, createError } = this
          const isValid = parseFloat(value) > 0

          return (
            isValid ||
            createError({
              path,
              message: i18n.t(
                'services::forms.register_account.avito.fields.budget.validation_messages.incorrect_amount_min',
                {
                  value: '0.00',
                  currency: 'RUB',
                },
              ),
            })
          )
        },
      )
      .required('common::validation_messages.required'),
  })

let CreateAvitoAccountForm = ({
  handleSubmit,
  submitting,
  onClose,
  autofill,
  networkId,
  form,
  networkType,
}) => {
  const { t } = useTranslation()
  const submitAccountData = useAccountCreateFormSubmit({ form, onClose, networkType })

  useEffect(() => {
    if (networkId) {
      autofill('network', networkId)
    }
  }, [networkId])

  return (
    <FormFrame
      onSubmit={handleSubmit(submitAccountData)}
      form={form}
      submitAccountData={submitAccountData}
      submitting={submitting}
      onClose={onClose}
    >
        <Field
          disabled
          name="network"
          hidden
          fullWidth
          component={renderInputField}
          t={t}
        />

        <div className={clsx('margin-bottom-16', formStyles['row_with-tooltip'])}>
          <Field
            fullWidth
            name="avito_user_id"
            required
            component={renderInputField}
            label={t('services::forms.register_account.avito.fields.avito_user_id.label')}
            format={(value) => (value === null ? '' : '' + value)}
            parse={(value) => Number(value.replace(/\D/g, '').slice(0, 50)) || null}
            t={t}
          />
        </div>

        <div className={clsx('margin-bottom-16', formStyles['row_with-tooltip'])}>
          <Field
            fullWidth
            name="tax_number"
            component={renderInputField}
            label={t('services::forms.register_account.avito.fields.tax_number.label')}
            format={(value) => (value === null ? '' : '' + value)}
            parse={onlyNumbers}
            maxLength={12}
            t={t}
          />
        </div>

        <div className={clsx('margin-bottom-16', formStyles['row_with-tooltip'])}>
          <Field
            label={t('services::forms.register_account.avito.fields.budget.label')}
            name="budget"
            type="text"
            component={renderAmountField}
            t={t}
            fullWidth
            autocomplete="off"
            format={(value) => (value === null ? '' : '' + value)}
            parse={(value) => value.replace(/\s/g, '').replace(',', '.')}
            simpleHelperStyle
            InputProps={{
              endAdornment: (
                <Typography
                  variant={'Body-25'}
                  noMargin
                  color={'Label-Secondary'}
                >
                  RUB
                </Typography>
              ),
            }}
          />
        </div>

    </FormFrame>
  )
}

CreateAvitoAccountForm = reduxForm({
  form: 'CreateAvitoAccountForm',
  initialValues: {
    network: '',
    avito_user_id: null,
    tax_number: null,
    budget: null,
  },
  shouldAsyncValidate: shouldAsyncValidate,
})(CreateAvitoAccountForm)

CreateAvitoAccountForm.propTypes = {
  handleSubmit: PropTypes.func,
  submitting: PropTypes.bool,
  onClose: PropTypes.func,
  autofill: PropTypes.func,
  networkId: PropTypes.string,
  initialValues: PropTypes.object,
  form: PropTypes.string,
  networkType: PropTypes.string,
}

export default CreateAvitoAccountForm
